import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  CreditCardOutlined,
  RollbackOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";

import styles from "./stylesPendingRequestsTable.module.css";

import { formatCurrency } from "../../../../utils/financialFormatters";
import CustomPagination from "../../../shared/CustomPagination/CustomPagination";
import { useWithdrawalReview } from "../WithdrawalReviewContext";
import {
  convertToUserTimezone,
  formatTimestampOrDisplay,
} from "../../../../utils/dateService";
import UserAvatar from "../../../CommonComponents/UserAvatar/UserAvatar";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import DataTable from "../../../shared/dashboards/DataTable/DataTable";
import {
  WalletBalanceCell,
  WalletColumnTitle,
} from "../../../shared/WalletBalanceCell";

function PendingRequestsTable({
  canReviewWithdrawal,
  canRefundWithdrawal,
  currencySymbol,
}) {
  const {
    pendingTransactions,
    handleViewUserFromPending,
    openDeclinedModal,
    openApproveModal,
    openRefundModal,
    openDetailsModal,
    openPaymentDetailsDrawer,

    pendingPageSize,
    setPendingPageSize,
    pendingCurrentPage,
    setPendingCurrentPage,
    pendingTotalCount,
    loadingPending,
    approveTransactionLoading,
    approvingTransactionId,
    refundTransactionLoading,
  } = useWithdrawalReview();

  const columns = [
    {
      title: AppStrings.REQUEST_DATE,
      dataIndex: "request_date_timestamp",
      key: "request_date",
      width: 160,
      render: (text) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (text, record) => (
        <div className={styles.userImage}>
          <UserAvatar />
          <button onClick={() => handleViewUserFromPending(record)}>
            <span>{text}</span>
          </button>
        </div>
      ),
    },
    {
      title: AppStrings.TRANSACTION_ID,
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 250,
    },
    {
      title: AppStrings.LAST_DEPOSIT_AMOUNT,
      dataIndex: "last_deposit_amount",
      key: "last_deposit_amount",
      width: 200,
      render: (text, record) => (
        <div className={styles.lastDepositCol}>
          <span>
            {formatCurrency(Number(text ?? 0), currencySymbol, 2)}
          </span>
          <span className={styles.lastDepositColDate}>
            {formatTimestampOrDisplay(
              record.last_deposit_date_timestamp,
              record.last_deposit_date,
              "MMM DD, HH:mm",
            )}
          </span>
        </div>
      ),
    },
    {
      title: AppStrings.WITHDRAWAL_AMOUNT,
      dataIndex: "withdrawal_amount",
      key: "withdrawal_amount",
      width: 200,
      render: (text) => (
        <span>
          {formatCurrency(Number(text ?? 0), currencySymbol, 2)}
        </span>
      ),
    },
    {
      title: <WalletColumnTitle />,
      dataIndex: "cash_balance",
      key: "wallet",
      width: 200,
      render: (_text, record) => (
        <WalletBalanceCell
          balance={record.cash_balance}
          percentChange7d={record.cash_balance_7d_change_pct}
        />
      ),
    },
    {
      title: AppStrings.FTW,
      dataIndex: "is_first_time_withdrawal",
      key: "is_first_time_withdrawal",
      width: 70,
      align: "center",
      render: (isFirstTimeWithdrawal) => (
        isFirstTimeWithdrawal === 1 || isFirstTimeWithdrawal === "1" || isFirstTimeWithdrawal === true ? (
          <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
        ) : (
          <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} />
        )
      ),
    },
    {
      title: AppStrings.PAYMENT_GATEWAY,
      dataIndex: "payment_gateway",
      key: "payment_gateway",
      width: 180,
    },
    {
      title: AppStrings.STATE,
      dataIndex: "subregion",
      key: "subregion",
      width: 140,
      render: (_, record) => {
        const v = record?.subregion;
        if (typeof v !== "string" || v.trim() === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{v}</span>;
      },
    },
    {
      title: AppStrings.METHOD,
      dataIndex: "method",
      key: "method",
      width: 160,
      render: (_, record) => {
        const v = record?.method ?? record?.withdrawal_method;
        if (typeof v !== "string" || v.trim() === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{v}</span>;
      },
    },
    {
      title: AppStrings.ACTIONS,
      key: "actions",
      width: canRefundWithdrawal ? 180 : 140,
      fixed: "right",
      render: (_, record) => (
        <div className={styles.actionButtonGroup}>
          <button
            className={`${styles.actionButton} ${styles.approve} ${!canReviewWithdrawal ? styles.disabled : ""}`}
            onClick={() => openApproveModal(record)}
            disabled={
              approveTransactionLoading ||
              refundTransactionLoading ||
              !canReviewWithdrawal
            }
          >
            {approveTransactionLoading &&
            approvingTransactionId === record.transaction_id ? (
              <RebetLoader />
            ) : (
              <CheckOutlined />
            )}
          </button>

          <button
            className={`${styles.actionButton} ${styles.reject} ${!canReviewWithdrawal ? styles.disabled : ""}`}
            onClick={() => openDeclinedModal(record)}
            disabled={
              approveTransactionLoading ||
              refundTransactionLoading ||
              !canReviewWithdrawal
            }
          >
            <CloseOutlined />
          </button>

          {canRefundWithdrawal && (
            <button
              type="button"
              aria-label={AppStrings.WITHDRAWAL_REFUND_ACTION}
              className={`${styles.actionButton} ${styles.refund}`}
              onClick={() => openRefundModal(record)}
              disabled={
                approveTransactionLoading ||
                refundTransactionLoading ||
                !canReviewWithdrawal
              }
            >
              <RollbackOutlined />
            </button>
          )}

          <button
            className={`${styles.actionButton} ${styles.details} ${!canReviewWithdrawal ? styles.disabled : ""}`}
            onClick={() => openDetailsModal(record)}
            disabled={!canReviewWithdrawal}
          >
            <EyeOutlined />
          </button>

          <button
            type="button"
            className={`${styles.actionButton} ${styles.details} ${!canReviewWithdrawal ? styles.disabled : ""}`}
            aria-label={AppStrings.PAYMENT_DETAILS}
            onClick={() => openPaymentDetailsDrawer(record)}
            disabled={!canReviewWithdrawal}
          >
            <CreditCardOutlined />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        loading={loadingPending}
        columns={columns}
        dataSource={pendingTransactions?.transactions || []}
        pagination={false}
        scrollPreset="xCompact"
        rowKey={(record) => record.transaction_id}
        className={styles.customTable}
      />
      <CustomPagination
        total={pendingTotalCount || 0}
        pageSize={pendingPageSize}
        currentPage={pendingCurrentPage}
        onPageSizeChange={(value) => {
          setPendingCurrentPage(1);
          setPendingPageSize(value);
        }}
        onPageChange={(page) => setPendingCurrentPage(page)}
      />
    </>
  );
}

export default PendingRequestsTable;
